import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { route } from 'preact-router';
import { MainLayout } from '../../components/MainLayout';
import { ThreadsSidebar } from '../../components/ThreadsSidebar';
import { WorkflowsSidebar } from '../../components/workflows/WorkflowsSidebar';
import { WorkflowForm } from '../../components/workflows/WorkflowForm';
import { WorkflowDashboard } from '../../components/workflows/WorkflowDashboard';
import { ChatInput } from '../../features/agents/components/ChatInput';
import { MessageList } from '../../features/agents/components/MessageList';
import { chatService } from '../../services/chat.service';
import { threadsService } from '../../services/threads.service';
import { agentsService } from '../../services/agents.service';
import { scheduledWorkflowsService } from '../../services/scheduled-workflows.service';
import { useAuth } from '../../hooks/useAuth';
import { useScopeContext } from '../../contexts/ScopeContext';
import { useThreadMessages } from '../../hooks/useThreadMessages';
import { toast } from 'react-toastify';
import type { Message, MessageContent } from '@zenmako/contracts/threads';
import type { CreateWorkflowInput } from '../../services/scheduled-workflows.service';

type SidebarTab = 'history' | 'workflows';

export function AgentChatPage({ agentId, threadId, workflowId }: { agentId?: string; threadId?: string; workflowId?: string }) {
  const { user } = useAuth();
  const scope = useScopeContext();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [agent, setAgent] = useState<any>(null);
  const [agents, setAgents] = useState<any[]>([]);
  const [workflow, setWorkflow] = useState<any>(null);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [executions, setExecutions] = useState<any[]>([]);
  const [executionsLoading, setExecutionsLoading] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [connectionCount, setConnectionCount] = useState(0);

  // Derive tab state from URL instead of local state
  const currentPath = window.location.pathname;
  const isWorkflowsRoute = currentPath.includes('/workflows');
  const isCreatingWorkflow = currentPath.endsWith('/new');
  const isViewingWorkflow = isWorkflowsRoute && !isCreatingWorkflow && !!workflowId && !threadId;
  const isViewingWorkflowExecution = isWorkflowsRoute && !!workflowId && !!threadId;
  const sidebarTab: SidebarTab = isWorkflowsRoute ? 'workflows' : 'history';

  // agentId should always come from path params now
  const effectiveAgentId = agentId || 'onemako';

  const handleFirstMessage = useCallback(() => {
    console.log('[AgentChatPage] First message received, refreshing thread list');
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Create a stable reference to the load messages function that will be populated later
  const loadMessagesRef = useRef<(() => Promise<void>) | null>(null);

  // Only use useThreadMessages when we have a threadId (history view)
  // chat/response@v1 events contain complete tool data including connectorId/toolId for approvals
  const { messages, setMessages } = useThreadMessages(
    threadId || 'placeholder',  // Use placeholder when no threadId (workflows view)
    handleFirstMessage
  );

  const loadMessages = useCallback(async () => {
    if (!threadId) {
      setMessagesLoading(false);
      return;
    }
    try {
      setMessagesLoading(true);
      const result = await threadsService.getMessages(scope.teamId, threadId);
      // Convert API messages to the format expected by MessageList
      const formattedMessages = result && result.length > 0
        ? result.map((msg: any) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.timestamp),
            agentId: msg.agentId,
            metadata: msg.metadata,
            toolCalls: msg.toolCalls
          }))
        : [];
      // Merge with existing state (WebSocket messages) to prevent race condition
      // where messages arriving via WebSocket during API load would be lost
      // IMPORTANT: API messages come FIRST so they take precedence during deduplication
      // API messages have complete data (connectorId, toolId) while WebSocket messages may be incomplete
      setMessages(prev => {
        const merged = [...formattedMessages, ...prev];

        // Helper to get first toolCallId from a message's tool calls
        const getToolCallId = (msg: Message): string | null => {
          const tc = msg.toolCalls?.[0];
          return tc?.id || null;
        };

        // Helper to check if a message has complete tool data (connectorId and toolId)
        const hasCompleteToolData = (msg: Message): boolean => {
          const tc = msg.toolCalls?.[0];
          const output = tc?.output as any;
          return !!(output?.connectorId && output?.toolId);
        };

        // Helper to normalize content by stripping [System: ...] prefix
        // DB stores messages with prefix, WebSocket sends without - need to normalize for comparison
        const normalizeContent = (content: string | MessageContent[]): string => {
          // Handle MessageContent[] by extracting text
          if (Array.isArray(content)) {
            return content
              .filter(c => c.type === 'text' && c.text)
              .map(c => c.text)
              .join(' ');
          }
          // Strip [System: xyz] prefix if present
          return content.replace(/^\[System:\s*[^\]]+\]\s*/, '');
        };

        // Helper to create a content fingerprint for deduplication
        // For text messages without tool calls, use role + normalized content
        const getContentFingerprint = (msg: Message): string | null => {
          // Only for messages without tool calls (text messages)
          if (msg.toolCalls && msg.toolCalls.length > 0) {
            return null;
          }
          if (!msg.content) {
            return null;
          }
          // Normalize content to handle [System: ...] prefix differences
          return `${msg.role}:${normalizeContent(msg.content)}`;
        };

        // Deduplicate by:
        // 1. message ID (exact match)
        // 2. toolCallId (for tool messages with different IDs but same tool call)
        // 3. content fingerprint (for text messages with same role+content)
        console.log('[loadMessages] Merging messages - API:', formattedMessages.length, 'WebSocket:', prev.length);
        const unique = merged.filter((msg, idx, self) => {
          // Check for earlier duplicate by message ID
          const earlierById = self.findIndex(m => m.id === msg.id);
          if (earlierById !== idx) {
            console.log('[loadMessages] Dedup by msgId:', msg.id);
            return false; // Skip this duplicate
          }

          // For tool messages, also check for earlier match by toolCallId
          const msgToolCallId = getToolCallId(msg);
          if (msgToolCallId) {
            const earlierByToolCallId = self.findIndex(m =>
              m !== msg && getToolCallId(m) === msgToolCallId
            );
            if (earlierByToolCallId !== -1 && earlierByToolCallId < idx) {
              // There's an earlier message with same toolCallId
              // Keep the one with complete data (prefer API over WebSocket)
              const earlier = self[earlierByToolCallId];
              const earlierComplete = hasCompleteToolData(earlier);
              const thisComplete = hasCompleteToolData(msg);

              console.log('[loadMessages] Dedup by toolCallId:', msgToolCallId, 'earlierComplete:', earlierComplete, 'thisComplete:', thisComplete);

              if (earlierComplete && !thisComplete) {
                return false; // Earlier has complete data, skip this one
              }
              if (!earlierComplete && thisComplete) {
                // This one has complete data but earlier doesn't - shouldn't happen
                // since API messages are first, but handle it
                return true;
              }
              // Both have same completeness - keep earlier (API message)
              return false;
            }
          }

          // For text messages (no tool calls), check for earlier match by content
          const msgFingerprint = getContentFingerprint(msg);
          if (msgFingerprint) {
            const earlierByContent = self.findIndex(m =>
              m !== msg && getContentFingerprint(m) === msgFingerprint
            );
            if (earlierByContent !== -1 && earlierByContent < idx) {
              console.log('[loadMessages] Dedup by content:', msg.id, 'matches earlier:', self[earlierByContent].id);
              return false; // Skip this duplicate - keep the earlier one (API message)
            }
          }

          return true;
        });

        // Sort by timestamp, with messageId as fallback for messages without createdAt
        return unique.sort((a, b) => {
          const aTime = a.timestamp?.getTime() ?? parseInt(a.id) ?? 0;
          const bTime = b.timestamp?.getTime() ?? parseInt(b.id) ?? 0;
          return aTime - bTime;
        });
      });
    } catch (err) {
      console.error('Failed to load messages:', err);
      // Only clear if we have no existing messages (initial load failure)
      setMessages(prev => prev.length === 0 ? [] : prev);
    } finally {
      setMessagesLoading(false);
    }
  }, [scope.teamId, threadId, setMessages]);

  // Update the ref when loadMessages changes
  loadMessagesRef.current = loadMessages;

  // Load data when IDs change - use refs to avoid re-fetching on every render
  // IMPORTANT: Don't include loadMessages in deps - we only want to load when threadId changes
  useEffect(() => {
    loadAgent();
    loadAgents();
    if (threadId && loadMessagesRef.current) {
      loadMessagesRef.current();
    }
    if (workflowId) {
      loadWorkflow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveAgentId, threadId, workflowId]);

  const loadAgent = async () => {
    try {
      const agentData = await agentsService.getAgent(scope.teamId, effectiveAgentId);
      setAgent(agentData);
    } catch (err) {
      console.error('Failed to load agent:', err);
    }
    try {
      const conns = await agentsService.getAgentConnections(scope.teamId, effectiveAgentId);
      setConnectionCount(conns.length);
    } catch {
      // ignore - non-critical
    }
  };

  const loadAgents = async () => {
    try {
      const agentsList = await agentsService.getAgents(scope.teamId);
      setAgents(agentsList);
    } catch (err) {
      console.error('Failed to load agents:', err);
    }
  };

  const loadWorkflow = async () => {
    if (!workflowId) return;
    try {
      setWorkflowLoading(true);
      const workflowData = await scheduledWorkflowsService.getWorkflow(scope.teamId, workflowId);
      setWorkflow(workflowData);
      // Also load execution history
      loadExecutionHistory();
    } catch (err) {
      console.error('Failed to load workflow:', err);
      toast.error('Failed to load workflow');
    } finally {
      setWorkflowLoading(false);
    }
  };

  const loadExecutionHistory = async () => {
    if (!workflowId) return;
    try {
      setExecutionsLoading(true);
      const history = await scheduledWorkflowsService.getWorkflowHistory(scope.teamId, workflowId, 10);
      setExecutions(history);
    } catch (err) {
      console.error('Failed to load execution history:', err);
    } finally {
      setExecutionsLoading(false);
    }
  };

  const handleSendMessage = async (content: string) => {
    if (!content.trim() || loading || !user || !threadId) return;

    setLoading(true);

    // Add user message immediately
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      // Extract the actual agent ID from the threadId (e.g., "oppie" from "oppie-xxx-xxx")
      const threadAgentId = threadId.split('-')[0];

      // Use generate endpoint (response comes via websocket)
      await chatService.sendMessageSimple(
        scope.teamId,  // teamId
        threadId,      // threadId
        content,       // message
        threadAgentId  // agentId
      );
      // Response will be delivered via websocket
      setLoading(false);
    } catch (error) {
      console.error('Generate error:', error);
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: `Error: ${error instanceof Error ? error.message : 'Failed to generate response'}`,
        timestamp: new Date()
      }]);
      setLoading(false);
    }
  };

  const handleCreateWorkflow = async (data: CreateWorkflowInput) => {
    try {
      await scheduledWorkflowsService.createWorkflow(scope.teamId, data);
      toast.success('Workflow created successfully');
      // Navigate back to workflows list view with agentId in path
      route(`/teams/${scope.teamId}/workflows/${effectiveAgentId}`);
      setRefreshTrigger(prev => prev + 1); // Refresh workflows list
    } catch (error) {
      console.error('Failed to create workflow:', error);
      throw error; // Let WorkflowForm handle the error
    }
  };

  const handleUpdateWorkflow = async (data: CreateWorkflowInput) => {
    if (!workflowId) return;
    try {
      await scheduledWorkflowsService.updateWorkflow(scope.teamId, workflowId, data);
      toast.success('Workflow updated successfully');
      // Navigate back to workflows list view with agentId in path
      route(`/teams/${scope.teamId}/workflows/${effectiveAgentId}`);
      setRefreshTrigger(prev => prev + 1); // Refresh workflows list
    } catch (error) {
      console.error('Failed to update workflow:', error);
      throw error; // Let WorkflowForm handle the error
    }
  };

  return (
    <MainLayout>
      <div class="flex flex-1 overflow-hidden">
        {/* Main Content Area - left/center */}
        <div class="flex-1 flex flex-col overflow-hidden">
          {sidebarTab === 'workflows' && isViewingWorkflowExecution ? (
            // Render workflow execution thread (like chat view with input)
            <>
              {/* Header with back button */}
              <div class="px-4 py-2 border-b border-base-300 bg-base-100">
                <div class="flex items-center justify-between">
                  <div class="text-sm text-base-content/60">
                    <span class="badge badge-sm badge-info mr-2">Workflow Execution</span>
                    {workflow?.name || 'Workflow Thread'}
                  </div>
                  <button
                    class="btn btn-ghost btn-xs"
                    onClick={() => route(`/teams/${scope.teamId}/workflows/${effectiveAgentId}/${workflowId}`)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M10 19l-7-7m0 0l7-7m-7 7h18"
                      />
                    </svg>
                    Back to Settings
                  </button>
                </div>
              </div>

              {messagesLoading ? (
                <div class="flex-1 flex items-center justify-center">
                  <span class="loading loading-spinner loading-lg"></span>
                </div>
              ) : (
                <MessageList
                  messages={messages}
                  agentName={agent?.name || effectiveAgentId}
                  threadId={threadId}
                  loading={loading}
                />
              )}

              <ChatInput
                onSend={handleSendMessage}
                disabled={loading || messagesLoading}
              />
            </>
          ) : sidebarTab === 'workflows' && isViewingWorkflow ? (
            // Render workflow edit form
            <div class="flex-1 overflow-y-auto p-6">
              <div class="max-w-2xl mx-auto">
                {workflowLoading ? (
                  <div class="flex justify-center py-8">
                    <span class="loading loading-spinner loading-lg"></span>
                  </div>
                ) : workflow ? (
                  <>
                    <div class="flex justify-between items-center mb-6">
                      <h2 class="text-2xl font-bold">Edit Workflow</h2>
                      <button
                        class="btn btn-ghost btn-sm"
                        onClick={() => route(`/teams/${scope.teamId}/workflows/${effectiveAgentId}`)}
                      >
                        Cancel
                      </button>
                    </div>

                    {/* Workflow Actions */}
                    <div class="flex gap-2 mb-6">
                      <button
                        class="btn btn-primary btn-sm"
                        onClick={async () => {
                          try {
                            const result = await scheduledWorkflowsService.runWorkflow(scope.teamId, workflow.workflowId);
                            toast.success('Workflow started - navigating to execution thread');
                            // Navigate to the workflow execution thread
                            setTimeout(() => {
                              route(`/teams/${scope.teamId}/workflows/${workflow.agentId}/${workflow.workflowId}/${result.threadId}`);
                            }, 500);
                          } catch (err) {
                            console.error('Failed to run workflow:', err);
                            toast.error('Failed to run workflow');
                          }
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                          />
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Run Now
                      </button>

                      {workflow.enabled ? (
                        <button
                          class="btn btn-warning btn-sm"
                          onClick={async () => {
                            try {
                              await scheduledWorkflowsService.pauseWorkflow(scope.teamId, workflow.workflowId);
                              toast.success('Workflow paused');
                              loadWorkflow();
                            } catch (err) {
                              console.error('Failed to pause workflow:', err);
                              toast.error('Failed to pause workflow');
                            }
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          Pause
                        </button>
                      ) : (
                        <button
                          class="btn btn-success btn-sm"
                          onClick={async () => {
                            try {
                              await scheduledWorkflowsService.resumeWorkflow(scope.teamId, workflow.workflowId);
                              toast.success('Workflow resumed');
                              loadWorkflow();
                            } catch (err) {
                              console.error('Failed to resume workflow:', err);
                              toast.error('Failed to resume workflow');
                            }
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                            />
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          Resume
                        </button>
                      )}

                      <button
                        class="btn btn-error btn-sm"
                        onClick={async () => {
                          if (!confirm('Are you sure you want to delete this workflow?')) return;
                          try {
                            await scheduledWorkflowsService.deleteWorkflow(scope.teamId, workflow.workflowId);
                            toast.success('Workflow deleted');
                            route(`/teams/${scope.teamId}/workflows/${effectiveAgentId}`);
                            setRefreshTrigger(prev => prev + 1);
                          } catch (err) {
                            console.error('Failed to delete workflow:', err);
                            toast.error('Failed to delete workflow');
                          }
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        Delete
                      </button>
                    </div>

                    <WorkflowForm
                      initialData={{
                        workflowId: workflow.workflowId,
                        agentId: workflow.agentId,
                        name: workflow.name,
                        description: workflow.description,
                        prompt: workflow.trigger.prompt,
                        schedule: workflow.schedule,
                        enabled: workflow.enabled,
                        primaryModel: workflow.primaryModel,
                        secondaryModel: workflow.secondaryModel,
                      }}
                      onSubmit={handleUpdateWorkflow}
                      onCancel={() => route(`/teams/${scope.teamId}/workflows/${effectiveAgentId}`)}
                      submitLabel="Update Workflow"
                    />

                    {/* Execution History */}
                    <div class="mt-8 pt-8 border-t border-base-300">
                      <h3 class="text-xl font-bold mb-4">Execution History</h3>
                      {executionsLoading ? (
                        <div class="flex justify-center py-8">
                          <span class="loading loading-spinner loading-md"></span>
                        </div>
                      ) : executions.length > 0 ? (
                        <div class="space-y-2">
                          {executions.map((execution: any) => (
                            <div
                              key={execution.scheduledFor}
                              class="card bg-base-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                              onClick={() => {
                                if (execution.threadId) {
                                  route(`/teams/${scope.teamId}/workflows/${workflow.agentId}/${workflow.workflowId}/${execution.threadId}`);
                                }
                              }}
                            >
                              <div class="card-body p-4">
                                <div class="flex items-center justify-between">
                                  <div class="flex items-center gap-3">
                                    <div class={`badge ${
                                      execution.status === 'completed' ? 'badge-success' :
                                      execution.status === 'failed' ? 'badge-error' :
                                      execution.status === 'running' ? 'badge-info' :
                                      'badge-warning'
                                    }`}>
                                      {execution.status}
                                    </div>
                                    <span class="text-sm">
                                      {new Date(execution.scheduledFor).toLocaleString()}
                                    </span>
                                    {execution.duration && (
                                      <span class="text-xs opacity-70">
                                        ({Math.round(execution.duration / 1000)}s)
                                      </span>
                                    )}
                                  </div>
                                  {execution.threadId && (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      class="h-5 w-5 opacity-50"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M9 5l7 7-7 7"
                                      />
                                    </svg>
                                  )}
                                </div>
                                {execution.error && (
                                  <div class="text-xs text-error mt-2">
                                    Error: {execution.error}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div class="text-center py-8 text-base-content/60">
                          <p>No execution history yet</p>
                          <p class="text-sm mt-1">Click "Run Now" to execute this workflow</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div class="alert alert-error">
                    <span>Workflow not found</span>
                  </div>
                )}
              </div>
            </div>
          ) : sidebarTab === 'workflows' && isCreatingWorkflow ? (
            // Render workflow creation form
            <div class="flex-1 overflow-y-auto p-6">
              <div class="max-w-2xl mx-auto">
                <div class="flex justify-between items-center mb-6">
                  <h2 class="text-2xl font-bold">Create New Workflow</h2>
                  <button
                    class="btn btn-ghost btn-sm"
                    onClick={() => route(`/teams/${scope.teamId}/workflows/${effectiveAgentId}`)}
                  >
                    Cancel
                  </button>
                </div>

                <WorkflowForm
                  initialData={{
                    agentId: effectiveAgentId,
                    name: '',
                    description: '',
                    prompt: '',
                    schedule: {
                      type: 'daily',
                      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    },
                    enabled: true,
                  }}
                  onSubmit={handleCreateWorkflow}
                  onCancel={() => route(`/teams/${scope.teamId}/workflows/${effectiveAgentId}`)}
                  submitLabel="Create Workflow"
                />
              </div>
            </div>
          ) : sidebarTab === 'workflows' ? (
            // Show workflow dashboard
            <WorkflowDashboard
              agentId={effectiveAgentId}
              onCreateWorkflow={() => route(`${scope.getNavigationPrefix()}/workflows/${effectiveAgentId}/new`)}
            />
          ) : !threadId ? (
            // Show empty state when no thread selected in history view
            <div class="flex-1 overflow-y-auto px-4 py-6">
              {connectionCount > 5 && (
                <div class="mx-4 mt-2 px-4 py-2 bg-warning/10 border border-warning/30 rounded-lg">
                  <div class="flex items-center gap-2 text-warning text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span>This agent has {connectionCount} connections. More than 5 can degrade AI performance.
                      <a href={`${scope.getNavigationPrefix()}/${effectiveAgentId}/settings?tab=connections`} class="link link-warning ml-1">Manage connections</a>
                    </span>
                  </div>
                </div>
              )}
              <div class="hero min-h-[40vh]">
                <div class="hero-content text-center">
                  <div class="max-w-md">
                    <h1 class="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                      Welcome to Zenmako!
                    </h1>
                    <p class="py-6">
                      Select a conversation from the sidebar to continue, or start a new conversation with {agent?.name || effectiveAgentId}.
                    </p>
                    <div class="flex flex-wrap gap-2 justify-center">
                      <div class="badge badge-outline">💡 Ask questions</div>
                      <div class="badge badge-outline">🔧 Use tools</div>
                      <div class="badge badge-outline">📝 Get assistance</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Render chat interface when thread is selected
            <>
              {connectionCount > 5 && (
                <div class="px-4 py-2 bg-warning/10 border-b border-warning/30">
                  <div class="flex items-center gap-2 text-warning text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span>This agent has {connectionCount} connections. More than 5 can degrade AI performance.
                      <a href={`${scope.getNavigationPrefix()}/${effectiveAgentId}/settings?tab=connections`} class="link link-warning ml-1">Manage connections</a>
                    </span>
                  </div>
                </div>
              )}
              {messagesLoading ? (
                <div class="flex-1 flex items-center justify-center">
                  <span class="loading loading-spinner loading-lg"></span>
                </div>
              ) : (
                <MessageList
                  messages={messages}
                  agentName={agent?.name || effectiveAgentId}
                  threadId={threadId}
                  loading={loading}
                />
              )}

              <ChatInput
                onSend={handleSendMessage}
                disabled={loading || messagesLoading}
              />
            </>
          )}
        </div>

        {/* Right Sidebar - Collapsible */}
        <div class={`flex flex-col bg-base-300 text-base-content border-l border-base-content/20 transition-all duration-300 ${sidebarCollapsed ? 'w-12' : 'w-80'}`}>
          {!sidebarCollapsed ? (
            <>
              {/* Agent Header with Inline Avatar and Collapse Button */}
              <div class="p-4 border-b border-base-content/10">
                <div class="flex items-center gap-3">
                  {/* Collapse Button */}
                  <button
                    onClick={() => setSidebarCollapsed(true)}
                    class="btn btn-ghost btn-sm btn-square hover:bg-base-content/10"
                    title="Collapse sidebar"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* Avatar - smaller, inline */}
                  <div class="avatar">
                    <div class="w-10 h-10 rounded-full bg-base-content/10 flex items-center justify-center">
                      <span class="text-xl">🤖</span>
                    </div>
                  </div>

                  {/* Agent Info and Dropdown */}
                  <div class="flex-1">
                    {agents.length > 1 ? (
                      <div class="dropdown dropdown-end w-full">
                        <div tabIndex={0} role="button" class="flex items-center justify-between cursor-pointer">
                          <h2 class="text-lg font-bold">{agent?.name || agentId}</h2>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                        <ul
                          tabIndex={0}
                          class="menu dropdown-content z-[1] p-2 shadow bg-base-100 rounded-box w-64 mt-2"
                        >
                          {agents.map((a) => (
                            <li key={a.id}>
                              <a
                                href={`${scope.getNavigationPrefix()}/chat/${a.id}`}
                                class={effectiveAgentId === a.id ? 'active' : ''}
                              >
                                <div class="flex flex-col items-start">
                                  <span class="font-semibold text-base-content">{a.name}</span>
                                  {a.description && (
                                    <span class="text-xs text-base-content/60 line-clamp-1">{a.description}</span>
                                  )}
                                </div>
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <h2 class="text-lg font-bold">{agent?.name || effectiveAgentId}</h2>
                    )}

                    {(agent?.description || agent?.role) && (
                      <p class="text-xs opacity-70 mt-1">{agent?.description || agent?.role}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Tab Buttons */}
              <div class="flex border-b border-base-content/10">
                <button
                  class={`flex-1 px-4 py-3 text-sm font-semibold transition-colors ${
                    sidebarTab === 'history'
                      ? 'bg-base-content/10 border-b-2 border-primary'
                      : 'hover:bg-base-content/5'
                  }`}
                  onClick={() => {
                    // Navigate to chat route (history view)
                    if (threadId) {
                      route(`/teams/${scope.teamId}/chat/${effectiveAgentId}/${threadId}`);
                    } else {
                      route(`/teams/${scope.teamId}/chat/${effectiveAgentId}`);
                    }
                  }}
                >
                  History
                </button>
                <button
                  class={`flex-1 px-4 py-3 text-sm font-semibold transition-colors ${
                    sidebarTab === 'workflows'
                      ? 'bg-base-content/10 border-b-2 border-primary'
                      : 'hover:bg-base-content/5'
                  }`}
                  onClick={() => {
                    // Navigate to workflows route with agentId in path
                    route(`/teams/${scope.teamId}/workflows/${effectiveAgentId}`);
                  }}
                >
                  Workflows
                </button>
              </div>

              {/* Tab Content - sidebars handle their own sizing */}
              {sidebarTab === 'history' ? (
                <ThreadsSidebar
                  agentId={effectiveAgentId}
                  agentName={agent?.name}
                  agentRole={agent?.description || agent?.role}
                  currentThreadId={threadId}
                  refreshTrigger={refreshTrigger}
                />
              ) : (
                <WorkflowsSidebar
                  agentId={effectiveAgentId}
                  agentName={agent?.name}
                  agentRole={agent?.description || agent?.role}
                  refreshTrigger={refreshTrigger}
                  onNewWorkflow={() => route(`/teams/${scope.teamId}/workflows/${effectiveAgentId}/new`)}
                  currentWorkflowId={workflowId}
                />
              )}
            </>
          ) : (
            /* Collapsed state - show expand button */
            <div class="flex flex-col items-center py-4 h-full">
              <button
                onClick={() => setSidebarCollapsed(false)}
                class="btn btn-ghost btn-sm btn-square hover:bg-base-content/10"
                title="Expand sidebar"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7M19 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Collapsed avatar */}
              <div class="mt-4">
                <div class="w-8 h-8 rounded-full bg-base-content/10 flex items-center justify-center">
                  <span class="text-sm">🤖</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
